import React, { useEffect, useState, useMemo, useCallback } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Calendar, Users, Clock, CheckCircle2, CircleDot, Hourglass, XCircle, ChevronLeft, ChevronRight } from "lucide-react";
import NewVisitModal, { serviceLabel } from "@/components/NewVisitModal";

const isoToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function rowStatus(r) {
  if (r.status === "closed" || r.status === "completed") return "completata";
  if (r.kind === "appt" && ["declined", "cancelled"].includes(r.confirmation)) return "annullata";
  if (r.kind === "appt" && r.starts_at && r.starts_at.slice(0, 16) > new Date().toISOString().slice(0, 16)) return "attesa";
  return "corso";
}

const STATUS_META = {
  attesa: { label: "In attesa", cls: "bg-slate-100 text-slate-600", Icon: Hourglass },
  corso: { label: "In corso / Bozza", cls: "bg-amber-100 text-amber-700", Icon: CircleDot },
  completata: { label: "Completata", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  annullata: { label: "Annullato", cls: "bg-red-100 text-red-600", Icon: XCircle },
};

function StatusChip({ s }) {
  const m = STATUS_META[s];
  return <span className={`${m.cls} px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1 w-fit`}><m.Icon size={11} /> {m.label}</span>;
}

function TodayRow({ r, sitesById, onOpen }) {
  const st = rowStatus(r);
  const site = r.site_id ? sitesById[r.site_id] : (r.sede_id ? sitesById[r.sede_id] : null);
  const siteName = site?.name || r.site_name || r.sede_snapshot?.name;
  const siteColor = site?.color || r.sede_snapshot?.color || "#94A3B8";
  return (
    <div className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 flex-wrap" data-testid={`today-row-${r.rowKey}`}>
      <span className="font-mono text-sm font-bold text-brand-navy w-14 shrink-0">{r.time || "—"}</span>
      <div className="flex-1 min-w-[180px]">
        <div className="font-extrabold text-brand-navy">{r.patient}</div>
        <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
          <span>{serviceLabel(r.service)}</span>
          {siteName && (
            <span className="inline-flex items-center gap-1 font-bold" style={{ color: siteColor }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: siteColor }}></span>{siteName}
            </span>
          )}
        </div>
      </div>
      <StatusChip s={st} />
      {r.visit_id ? (
        <button data-testid={`today-open-${r.rowKey}`} onClick={() => onOpen(r.visit_id)} className="bg-brand-azure hover:bg-brand-navy text-white text-xs font-bold px-3 py-1.5 rounded-lg transition">
          Apri cartella
        </button>
      ) : (
        <span className="text-xs text-slate-400 italic">Nessuna cartella</span>
      )}
    </div>
  );
}

export default function Visite() {
  const [appts, setAppts] = useState([]);
  const [visits, setVisits] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(searchParams.get("new") === "1");
  const [day, setDay] = useState(() => {
    const d = searchParams.get("date");
    return d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : isoToday();
  });
  const nav = useNavigate();
  const isToday = day === isoToday();

  // Deep-link ?date= anche a componente già montato (azioni assistente)
  useEffect(() => {
    const d = searchParams.get("date");
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) setDay(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const shiftDay = (delta) => {
    const [y, m, dd] = day.split("-").map(Number);
    const d = new Date(y, m - 1, dd + delta);
    setDay(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
  };

  const dayLabel = (() => {
    const [y, m, dd] = day.split("-").map(Number);
    return new Date(y, m - 1, dd).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, v] = await Promise.allSettled([
        api.get("/medico/appointments", { params: { date_from: day, date_to: day } }),
        api.get("/visits", { params: { date_from: day, date_to: day } }),
      ]);
      setAppts(a.status === "fulfilled" ? (a.value.data || []) : []);
      setVisits(v.status === "fulfilled" ? (v.value.data || []) : []);
    } finally { setLoading(false); }
  }, [day]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/medico/sites").then(({ data }) => setSites((data || []).filter((s) => s.active !== false))).catch(() => {});
  }, []);

  const sitesById = useMemo(() => {
    const m = {};
    sites.forEach((s) => { m[s.id] = s; });
    return m;
  }, [sites]);

  const rows = useMemo(() => {
    const out = [];
    const apptVisitIds = new Set();
    appts.forEach((a) => {
      if (a.visit_id) apptVisitIds.add(a.visit_id);
      out.push({
        kind: "appt", rowKey: `a-${a.id}`, time: (a.starts_at || "").slice(11, 16),
        starts_at: a.starts_at, patient: a.patient_full_name,
        service: a.service, status: a.status, visit_id: a.visit_id,
        confirmation: a.confirmation,
        site_id: a.site_id, site_name: a.site_name,
      });
    });
    visits.forEach((v) => {
      if (apptVisitIds.has(v.id)) return;
      out.push({
        kind: "visit", rowKey: `v-${v.id}`,
        time: new Date(v.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }),
        starts_at: v.created_at, patient: `${v.patient_name || ""} ${v.patient_surname || ""}`.trim(),
        service: v.service, status: v.status, visit_id: v.id,
        sede_id: v.sede_id, sede_snapshot: v.sede_snapshot,
      });
    });
    out.sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    return statusFilter ? out.filter((r) => rowStatus(r) === statusFilter) : out;
  }, [appts, visits, statusFilter]);

  const counts = useMemo(() => {
    const all = [];
    const apptVisitIds = new Set(appts.filter((a) => a.visit_id).map((a) => a.visit_id));
    appts.forEach((a) => all.push(rowStatus({ kind: "appt", starts_at: a.starts_at, status: a.status, confirmation: a.confirmation })));
    visits.forEach((v) => { if (!apptVisitIds.has(v.id)) all.push(rowStatus({ kind: "visit", status: v.status })); });
    return {
      tot: all.length,
      attesa: all.filter((s) => s === "attesa").length,
      corso: all.filter((s) => s === "corso").length,
      completata: all.filter((s) => s === "completata").length,
      annullata: all.filter((s) => s === "annullata").length,
    };
  }, [appts, visits]);

  const closeModal = () => {
    setOpen(false);
    if (searchParams.get("new")) { searchParams.delete("new"); setSearchParams(searchParams, { replace: true }); }
  };

  return (
    <AppShell
      title={isToday ? "Visite di oggi" : "Visite del giorno"}
      subtitle={dayLabel}
      rightSlot={<button data-testid="open-new-visit" onClick={() => setOpen(true)} className="bg-brand-orange hover:bg-brand-orangeDark text-white font-bold px-4 py-2.5 rounded-lg flex items-center gap-2"><Plus size={16} /> Nuova visita</button>}
    >
      {/* Vista giornaliera navigabile: frecce, selettore data, tasto Oggi */}
      <div className="flex flex-wrap items-center gap-2 mb-4 bg-white border border-[#E2E8F0] rounded-xl px-3 py-2.5" data-testid="day-nav-bar">
        <button data-testid="day-prev" onClick={() => shiftDay(-1)} title="Giorno precedente"
          className="p-2 rounded-lg border-2 border-[#E2E8F0] hover:border-brand-azure hover:bg-brand-azure/10 text-brand-navy transition">
          <ChevronLeft size={18} />
        </button>
        <input type="date" data-testid="day-picker" value={day}
          onChange={(e) => e.target.value && setDay(e.target.value)}
          className="px-3 py-2 rounded-lg border-2 border-[#E2E8F0] text-sm font-bold text-brand-navy focus:outline-none focus:border-brand-azure" />
        <button data-testid="day-next" onClick={() => shiftDay(1)} title="Giorno successivo"
          className="p-2 rounded-lg border-2 border-[#E2E8F0] hover:border-brand-azure hover:bg-brand-azure/10 text-brand-navy transition">
          <ChevronRight size={18} />
        </button>
        <button data-testid="day-today" onClick={() => setDay(isoToday())} disabled={isToday}
          className={`px-4 py-2 rounded-lg text-sm font-bold transition ${isToday ? "bg-slate-100 text-slate-400 cursor-default" : "bg-brand-navy hover:bg-brand-navyDark text-white"}`}>
          Oggi
        </button>
        <span className="text-sm font-extrabold text-brand-navy capitalize ml-1" data-testid="day-nav-label">{dayLabel}</span>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="today-status-filters">
        {[
          { k: "", label: `Tutte (${counts.tot})` },
          { k: "attesa", label: `In attesa (${counts.attesa})` },
          { k: "corso", label: `In corso (${counts.corso})` },
          { k: "completata", label: `Completate (${counts.completata})` },
          ...(counts.annullata > 0 ? [{ k: "annullata", label: `Annullate (${counts.annullata})` }] : []),
        ].map((f) => (
          <button key={f.k || "all"} data-testid={`today-filter-${f.k || "all"}`} onClick={() => setStatusFilter(f.k)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition ${statusFilter === f.k ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-600 border-[#E2E8F0] hover:border-brand-azure"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white border-2 border-brand-navy/15 rounded-xl overflow-hidden" data-testid="today-visits-panel">
        <div className="px-4 py-3 bg-brand-navy/5 border-b border-[#E2E8F0] flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse"></span>
          <h2 className="font-extrabold text-brand-navy text-sm uppercase tracking-wide flex items-center gap-1.5"><Clock size={14} /> Lista ambulatoriale</h2>
          <span className="text-xs text-slate-500">{loading ? "…" : `${rows.length} in lista`}</span>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-slate-400">Caricamento…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center" data-testid="today-visits-empty">
            <div className="font-bold text-brand-navy">{isToday ? "Nessuna visita in programma oggi" : `Nessuna visita in programma per ${dayLabel}`}</div>
            <div className="text-sm text-slate-500 mt-1">Usa <b>Nuova visita</b> per un paziente in studio, oppure programma dall'<b>Agenda</b>.</div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((r) => <TodayRow key={r.rowKey} r={r} sitesById={sitesById} onOpen={(vid) => nav(`/visite/${vid}`)} />)}
          </div>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button data-testid="today-goto-agenda" onClick={() => nav("/agenda")} className="bg-white border-2 border-[#E2E8F0] hover:border-brand-azure rounded-xl p-4 flex items-center gap-3 text-left transition">
          <Calendar className="text-brand-azure shrink-0" size={20} />
          <div>
            <div className="font-extrabold text-brand-navy text-sm">Agenda</div>
            <div className="text-xs text-slate-500">Programma o sposta appuntamenti dal calendario</div>
          </div>
        </button>
        <button data-testid="today-goto-pazienti" onClick={() => nav("/pazienti?tab=visite")} className="bg-white border-2 border-[#E2E8F0] hover:border-brand-azure rounded-xl p-4 flex items-center gap-3 text-left transition">
          <Users className="text-brand-azure shrink-0" size={20} />
          <div>
            <div className="font-extrabold text-brand-navy text-sm">Storico completo</div>
            <div className="text-xs text-slate-500">Tutte le visite passate con filtri, in Pazienti</div>
          </div>
        </button>
      </div>

      <NewVisitModal open={open} onClose={closeModal} sites={sites} onCreated={(v) => { closeModal(); nav(`/visite/${v.id}`); }} />
    </AppShell>
  );
}
